import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { BookOpen, ClipboardList, Eye, Loader2, LogOut, Pencil, Trash2 } from "lucide-react";
import { getJson } from "@/lib/api";
import { postJson, useAuth } from "@/lib/auth";
import { locDoc, useLang } from "@/lib/i18n";
import { itemImage } from "@/components/catalog/ItemCard";
import { Avatar } from "@/components/community/shared";
import { Post, useWallState } from "@/components/community/Wall";
import { ProfileEditor } from "@/components/profile/ProfileEditor";
import { profileProgress } from "@/pages/ProfileQuiz";
import { NewsCard, useNewsSaved } from "@/components/news/NewsCard";

const SavedNews = () => {
  const [news, setNews] = useState([]);
  const { saved, toggle } = useNewsSaved();
  useEffect(() => {
    getJson("/news-saved").then((d) => setNews(Array.isArray(d) ? d : []));
  }, []);
  const shown = news.filter((n) => saved.includes(n.id));
  if (!shown.length) return null;
  return (
    <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="profile-saved-news">
      {shown.map((n) => <NewsCard key={n.id} n={n} saved onSave={toggle} />)}
    </div>
  );
};

const QUIZ_TOTAL = 5;

const CompleteBanner = ({ member }) => {
  const { t } = useLang();
  const n = profileProgress(member);
  if (n >= QUIZ_TOTAL) return null;
  return (
    <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[var(--green)]/40 bg-[var(--green)]/[0.06] p-5" data-testid="profile-complete-banner">
      <div className="min-w-0 flex-1">
        <p className="font-display text-2xl">{t("quiz.banner.title")}</p>
        <p className="mt-1 text-sm text-[var(--muted)]">{t("quiz.banner.body", { n, total: QUIZ_TOTAL })}</p>
        <div className="mt-3 h-1.5 max-w-sm overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[var(--green)]" style={{ width: `${(n / QUIZ_TOTAL) * 100}%` }} /></div>
      </div>
      <Link to="/perfil/cuestionario" className="btn-primary" data-testid="profile-complete-btn"><ClipboardList size={16} /> {t("quiz.banner.cta")}</Link>
    </div>
  );
};

const SavedPosts = () => {
  const { t } = useLang();
  const [posts, setPosts] = useState(null);
  const ws = useWallState(setPosts);
  useEffect(() => {
    getJson("/community/me/saved").then((d) => setPosts(Array.isArray(d) ? d : []));
  }, []);
  const shown = (posts ?? []).filter((p) => ws.saved.includes(p.id));
  const anySaved = ws.saved.length > 0;
  return (
    <section className="mt-16" data-testid="profile-saved">
      <h2 className="font-display text-3xl sm:text-4xl">{t("save.title")}</h2>
      <p className="mono mt-2 text-[11px] text-[var(--muted)]">{t("save.private")}</p>
      <SavedNews />
      {posts === null ? <Loader2 className="mx-auto mt-8 animate-spin" /> : !shown.length ? (
        !anySaved && <p className="mt-6 text-sm text-[var(--muted)]" data-testid="profile-saved-empty">{t("save.empty")}</p>
      ) : (
        <div className="mt-6 gap-4 sm:columns-2 lg:columns-3">{shown.map((p) => <Post key={p.id} p={p} ws={ws} />)}</div>
      )}
    </section>
  );
};

const NameEditor = ({ member, onSaved }) => {
  const { t } = useLang();
  const [edit, setEdit] = useState(false);
  return (
    <>
      <h1 className="font-display flex items-center gap-3 text-4xl leading-tight sm:text-5xl" data-testid="profile-name">
        {member.name || t("profile.noName")}
        <button type="button" onClick={() => setEdit(true)} className="icon-btn" aria-label={t("prof.edit")} data-testid="profile-edit-btn"><Pencil size={15} /></button>
      </h1>
      {edit && <ProfileEditor member={member} onSaved={onSaved} open={edit} setOpen={setEdit} />}
    </>
  );
};

const MyItem = ({ it, onRemove }) => {
  const { t } = useLang();
  return (
    <div className="rounded-2xl border border-white/10 bg-[var(--card)] p-3" data-testid={`my-item-${it.id}`}>
      <div className="grid aspect-square place-items-center overflow-hidden rounded-xl bg-[#e4dccb]">
        <img src={itemImage(it)} alt={it.name} className={`h-full w-full object-contain p-4 ${it.image_url ? "" : "opacity-60"}`} loading="lazy" />
      </div>
      <p className="font-display mt-3 line-clamp-2 text-lg leading-tight">{it.name}</p>
      <p className="mt-1 truncate text-xs text-[var(--muted)]">{it.manufacturer} · {it.year} · {t(`cat.${it.category}`)}</p>
      <button type="button" onClick={() => onRemove(it.id)} className="mono mt-3 inline-flex items-center gap-1 text-[11px] text-[#ff8a7a]" data-testid={`my-item-remove-${it.id}`}><Trash2 size={12} /> {t("profile.remove")}</button>
    </div>
  );
};

export default function Profile() {
  const { t, lang } = useLang();
  const { member, setMember, logout } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (member) getJson("/members/me/items").then((d) => setItems(Array.isArray(d) ? d : []));
  }, [member]);
  if (member === null) return <main className="grid min-h-screen place-items-center"><Loader2 className="animate-spin" /></main>;
  if (!member) return <Navigate to="/entrar?next=/perfil" replace />;
  if (member.onboarded === false) return <Navigate to="/perfil/cuestionario" replace />;
  const remove = async (id) => {
    const r = await postJson(`/members/me/items/${id}`, null, "DELETE");
    setMember({ ...member, item_ids: r.item_ids });
    setItems((s) => s.filter((x) => x.id !== id));
  };
  return (
    <main className="px-5 pb-28 pt-32 md:px-10" data-testid="profile-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <Avatar author={member} size={64} />
            <div>
              <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("profile.kicker")}</p>
              <NameEditor member={member} onSaved={setMember} />
              <p className="mt-1 text-sm text-[var(--muted)]" data-testid="profile-email">{member.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {member.handle && <Link to={`/u/${member.handle}`} className="btn-ghost" data-testid="profile-public-link"><Eye size={16} /> {t("prof.viewPublic")}</Link>}
            <Link to="/perfil/cuestionario" className="btn-ghost" data-testid="profile-quiz-link"><ClipboardList size={16} /> {t("quiz.open")}</Link>
            <button type="button" onClick={logout} className="btn-ghost" data-testid="profile-logout-btn"><LogOut size={16} /> {t("profile.logout")}</button>
          </div>
        </div>
        <CompleteBanner member={member} />
        <div className="mt-14 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl">{t("profile.inventory")}</h2>
            <p className="mono mt-2 text-[11px] text-[var(--muted)]" data-testid="profile-count">{t("cat.pieces", { n: member.item_ids?.length ?? 0 })}</p>
          </div>
          <Link to="/catalogo/revista" className="btn-primary" data-testid="profile-open-catalog-btn"><BookOpen size={16} /> {t("profile.addMore")}</Link>
        </div>
        {items === null && <Loader2 className="mx-auto mt-16 animate-spin" />}
        {items && !items.length && (
          <div className="mt-8 rounded-2xl border border-dashed border-white/20 px-6 py-16 text-center" data-testid="profile-empty">
            <p className="font-display text-3xl">{t("profile.empty")}</p>
            <p className="mt-2 text-sm text-[var(--muted)]">{t("profile.emptyHint")}</p>
          </div>
        )}
        {items && items.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="profile-items">
            {items.map((it) => <MyItem key={it.id} it={locDoc(it, lang)} onRemove={remove} />)}
          </div>
        )}
        <SavedPosts />
      </div>
    </main>
  );
}
